import React, { useRef, useEffect } from 'react';
import { setupCanvas } from './canvasUtils';

// ============================================
// Chapter 13  -  Particle Decay Physics
// ============================================

/** 13.2 – Deriving the Exponential Decay Law */
export const ExponentialDecayLaw = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Exponential Decay Law', W / 2, 24);

        // Derivation steps
        const steps = [
            { label: 'Rate equation', eq: 'dN/dt = −λN', color: '#3B82F6' },
            { label: 'Integrate', eq: '∫dN/N = −λ∫dt', color: '#22C55E' },
            { label: 'Solution', eq: 'N(t) = N₀ e^(−λt)', color: '#EF4444' },
        ];

        steps.forEach((s, i) => {
            const x = 20 + i * 185, y = 48, w = 168, h = 68;
            ctx.fillStyle = s.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
            ctx.strokeStyle = s.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
            ctx.fillStyle = s.color; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(s.label, x + w / 2, y + 18);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 16px Inter,sans-serif';
            ctx.fillText(s.eq, x + w / 2, y + 48);
            if (i < steps.length - 1) {
                ctx.fillStyle = '#94A3B8'; ctx.font = 'bold 18px Inter,sans-serif';
                ctx.fillText('→', x + w + 6, y + h / 2 + 4);
            }
        });

        // Decay plot
        const m = { left: 70, right: 30, top: 135, bottom: 50 };
        const pw = W - m.left - m.right, ph = H - m.top - m.bottom;

        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(m.left, m.top); ctx.lineTo(m.left, m.top + ph); ctx.lineTo(m.left + pw, m.top + ph); ctx.stroke();

        ctx.save(); ctx.translate(20, m.top + ph / 2); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('N / N₀', 0, 0); ctx.restore();
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Time (units of τ = 1/λ)', m.left + pw / 2, H - 8);

        // Curve
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 3;
        ctx.beginPath();
        for (let px = 0; px <= pw; px++) {
            const t = (px / pw) * 5;
            const frac = Math.exp(-t);
            const x = m.left + px;
            const y = m.top + ph - frac * ph;
            px === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();

        // τ marker
        const tauX = m.left + pw / 5;
        const tauY = m.top + ph - Math.exp(-1) * ph;
        ctx.setLineDash([3, 3]); ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(tauX, tauY); ctx.lineTo(m.left, tauY); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(tauX, tauY); ctx.lineTo(tauX, m.top + ph); ctx.stroke();
        ctx.setLineDash([]);
        ctx.beginPath(); ctx.arc(tauX, tauY, 4, 0, Math.PI * 2); ctx.fillStyle = '#EF4444'; ctx.fill();

        ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('1/e ≈ 37%', m.left - 4, tauY + 4);
        ctx.textAlign = 'center'; ctx.fillText('τ', tauX, m.top + ph + 14);

        // Y labels
        [1.0, 0.5].forEach(v => {
            const y = m.top + ph - v * ph;
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(v.toFixed(1), m.left - 6, y + 4);
        });
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 13.3 – Mean Lifetime τ */
export const MeanLifetimeTau = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Mean Lifetime τ  -  Key Particle Lifetimes', W / 2, 24);

        // Formula
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif';
        ctx.fillText('τ = 1/λ = 1/Γ', W / 2, 48);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('(τ = mean lifetime, λ = decay constant, Γ = decay width)', W / 2, 66);

        const particles = [
            { name: 'Free neutron', tau: '~880 s', bar: 0.95, color: '#3B82F6' },
            { name: 'Muon (μ)', tau: '2.2 μs', bar: 0.7, color: '#22C55E' },
            { name: 'Charged pion (π±)', tau: '26 ns', bar: 0.5, color: '#F59E0B' },
            { name: 'Neutral kaon (K⁰_S)', tau: '90 ps', bar: 0.35, color: '#EF4444' },
            { name: 'W boson', tau: '~3�-10⁻²⁵ s', bar: 0.08, color: '#8B5CF6' },
            { name: 'Z boson', tau: '~2.6�-10⁻²⁵ s', bar: 0.06, color: '#EC4899' },
        ];

        const bx = 180, bw = 320, by = 70;
        particles.forEach((p, i) => {
            const y = by + i * 28;
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(p.name, bx - 8, y + 14);

            ctx.fillStyle = p.color + '30'; ctx.beginPath(); ctx.roundRect(bx, y + 2, bw, 18, 4); ctx.fill();
            ctx.fillStyle = p.color; ctx.beginPath(); ctx.roundRect(bx, y + 2, bw * p.bar, 18, 4); ctx.fill();

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(p.tau, bx + bw * p.bar + 6, y + 15);
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('(log scale  -  bars represent relative orders of magnitude)', W / 2, by + 6 * 28 + 12);
        ctx.fillText('After one mean lifetime, 1/e ≈ 36.8% of the original particles remain', W / 2, by + 6 * 28 + 28);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-pink-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 13.4 – Half-Life and Its Relation to Lifetime */
export const HalfLifeRelation = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 240;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Half-Life vs Mean Lifetime', W / 2, 24);

        // Key relationship
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 20px Inter,sans-serif';
        ctx.fillText('t₁/₂ = τ · ln 2 ≈ 0.693 τ', W / 2, 58);

        // Visual comparison
        const cards = [
            { title: 'Half-Life (t₁/₂)', desc: 'Time for N to\nfall to N₀/2', value: '50% remain', formula: 'N(t₁/₂) = N₀/2', color: '#3B82F6' },
            { title: 'Mean Lifetime (τ)', desc: 'Average time a\nparticle survives', value: '36.8% remain', formula: 'N(τ) = N₀/e', color: '#22C55E' },
            { title: 'Decay Constant (λ)', desc: 'Probability of decay\nper unit time', value: 'λ = 1/τ', formula: 'λ = ln 2 / t₁/₂', color: '#F59E0B' },
        ];

        cards.forEach((cd, i) => {
            const x = 12 + i * 178, y = 75, w = 165, h = 115;
            ctx.fillStyle = cd.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
            ctx.strokeStyle = cd.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();

            ctx.fillStyle = cd.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(cd.title, x + w / 2, y + 20);

            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            cd.desc.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 40 + li * 14));

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif';
            ctx.fillText(cd.value, x + w / 2, y + 80);
            ctx.fillStyle = cd.color; ctx.font = '11px Inter,sans-serif';
            ctx.fillText(cd.formula, x + w / 2, y + 98);
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Physicists use τ (mean lifetime); nuclear scientists prefer t₁/₂ (half-life)', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={240} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 13.5 – Kaon Decay Modes (case study) */
export const KaonDecayModes = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Kaon Decay Modes  -  Branching Ratios', W / 2, 24);

        // K+ decay modes
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('K⁺ (τ = 12.4 ns)', 150, 54);

        const kpModes = [
            { mode: 'K⁺ → μ⁺ νμ', br: 63.6, color: '#3B82F6' },
            { mode: 'K⁺ → π⁺ π⁰', br: 20.7, color: '#22C55E' },
            { mode: 'K⁺ → π⁺ π⁺ π⁻', br: 5.6, color: '#F59E0B' },
            { mode: 'K⁺ → π⁰ e⁺ νₑ', br: 5.1, color: '#EF4444' },
            { mode: 'Other', br: 5.0, color: '#94A3B8' },
        ];

        // Pie chart for K+
        let startAngle = -Math.PI / 2;
        const pcx = 150, pcy = 145, pr = 65;
        kpModes.forEach(m => {
            const sweep = (m.br / 100) * Math.PI * 2;
            ctx.beginPath(); ctx.moveTo(pcx, pcy);
            ctx.arc(pcx, pcy, pr, startAngle, startAngle + sweep);
            ctx.closePath(); ctx.fillStyle = m.color; ctx.fill();
            ctx.strokeStyle = 'white'; ctx.lineWidth = 1.5; ctx.stroke();
            startAngle += sweep;
        });

        // Legend for K+
        kpModes.forEach((m, i) => {
            const y = 66 + i * 18;
            ctx.fillStyle = m.color; ctx.fillRect(230, y, 10, 10);
            ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(m.mode + ' (' + m.br + '%)', 244, y + 9);
        });

        // K0S and K0L comparison
        ctx.fillStyle = '#F59E0B'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('K⁰ System', 450, 54);

        const k0Data = [
            { name: 'K⁰_S', tau: 'τ = 90 ps', modes: 'π⁺π⁻ (69%)\nπ⁰π⁰ (31%)', color: '#F59E0B' },
            { name: 'K⁰_L', tau: 'τ = 51 ns', modes: 'π±e∓ν (41%)\nπ±μ∓ν (27%)\n3π (32%)', color: '#8B5CF6' },
        ];

        k0Data.forEach((k, i) => {
            const x = 355 + i * 100, y = 68, w = 100, h = 130;
            ctx.fillStyle = k.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); ctx.fill();
            ctx.strokeStyle = k.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); ctx.stroke();

            ctx.fillStyle = k.color; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(k.name, x + w / 2, y + 18);
            ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(k.tau, x + w / 2, y + 36);
            k.modes.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 56 + li * 14));
        });

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('K⁰_S lifetime is 570�- shorter than K⁰_L  -  a factor of 570 between CP eigenstates!', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Kaon mixing and CP violation were key discoveries in particle physics', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 13.6 – Relativistic Decay Length */
export const DecayLengthSurvival = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Relativistic Decay Length', W / 2, 24);

        // Key formulas
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 16px Inter,sans-serif';
        ctx.fillText('d = βγcτ     Survival: P(L) = e^(−L/d)', W / 2, 56);

        // Diagram: particle flying, decaying at distance d
        const dy = 105;
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(50, dy); ctx.lineTo(350, dy); ctx.stroke();
        // Dashed continuation
        ctx.setLineDash([5, 4]); ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(350, dy); ctx.lineTo(490, dy); ctx.stroke();
        ctx.setLineDash([]);

        // Production vertex
        ctx.beginPath(); ctx.arc(50, dy, 6, 0, Math.PI * 2); ctx.fillStyle = '#22C55E'; ctx.fill();
        ctx.fillStyle = '#22C55E'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Production', 50, dy + 22);

        // Decay vertex
        ctx.beginPath(); ctx.arc(350, dy, 6, 0, Math.PI * 2); ctx.fillStyle = '#EF4444'; ctx.fill();
        ctx.fillStyle = '#EF4444'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Decay', 350, dy + 22);

        // Decay products
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(350, dy); ctx.lineTo(430, dy - 30); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(350, dy); ctx.lineTo(430, dy + 30); ctx.stroke();
        ctx.fillStyle = '#F59E0B'; ctx.font = '10px Inter,sans-serif';
        ctx.fillText('products', 460, dy);

        // d arrow
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(50, dy - 20); ctx.lineTo(350, dy - 20); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(50, dy - 23); ctx.lineTo(50, dy - 17); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(350, dy - 23); ctx.lineTo(350, dy - 17); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('d = βγcτ (mean decay length)', 200, dy - 28);

        // Example table
        const ty = 145;
        ctx.fillStyle = '#1E293B'; ctx.beginPath(); ctx.roundRect(30, ty, 480, 24, [8, 8, 0, 0]); ctx.fill();
        ['Particle', 'τ (rest)', 'γ (typical)', 'cτ', 'd = βγcτ'].forEach((h, i) => {
            const x = [70, 170, 260, 345, 440][i];
            ctx.fillStyle = 'white'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(h, x, ty + 17);
        });

        const examples = [
            ['π±', '26 ns', '100', '7.8 m', '~780 m'],
            ['K±', '12 ns', '50', '3.7 m', '~185 m'],
            ['B±', '1.6 ps', '10', '0.5 mm', '~5 mm'],
        ];

        examples.forEach((row, ri) => {
            const y = ty + 24 + ri * 22;
            ctx.fillStyle = ri % 2 === 0 ? '#F8FAFC' : 'white'; ctx.fillRect(30, y, 480, 22);
            row.forEach((cell, ci) => {
                ctx.fillStyle = ci === 0 ? '#3B82F6' : '#475569';
                ctx.font = ci === 0 ? 'bold 11px Inter,sans-serif' : '11px Inter,sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText(cell, [70, 170, 260, 345, 440][ci], y + 16);
            });
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Time dilation (γ) stretches decay length  -  fast particles travel further before decaying', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 13.10 – Branching Ratios and Partial Widths */
export const BranchingRatios = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Branching Ratios & Partial Widths', W / 2, 24);

        // Formulas
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('Γ_total = ΣΓᵢ     BR(i) = Γᵢ / Γ_total     Σ BR(i) = 1', W / 2, 52);

        // Z boson example (well-known branching ratios)
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('Example: Z⁰ Boson Decay', W / 2, 80);

        const modes = [
            { mode: 'Hadrons (qq\u0304)', br: 69.9, color: '#3B82F6' },
            { mode: 'Invisible (νν\u0304)', br: 20.0, color: '#8B5CF6' },
            { mode: 'e⁺e⁻', br: 3.36, color: '#EF4444' },
            { mode: 'μ⁺μ⁻', br: 3.37, color: '#22C55E' },
            { mode: 'τ⁺τ⁻', br: 3.37, color: '#F59E0B' },
        ];

        // Stacked bar
        const barX = 60, barY = 95, barW = 420, barH = 30;
        let cumX = barX;
        modes.forEach(m => {
            const w = (m.br / 100) * barW;
            ctx.fillStyle = m.color; ctx.fillRect(cumX, barY, w, barH);
            cumX += w;
        });
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 1; ctx.strokeRect(barX, barY, barW, barH);

        // Legend
        modes.forEach((m, i) => {
            const col = i < 3 ? 0 : 1;
            const row = i < 3 ? i : i - 3;
            const lx = 60 + col * 240, ly = 140 + row * 20;
            ctx.fillStyle = m.color; ctx.fillRect(lx, ly, 12, 12);
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(m.mode + '  -  ' + m.br + '%', lx + 18, ly + 10);
        });

        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(60, 210, 420, 25, 6); ctx.fill();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Γ_Z = 2.495 GeV → τ_Z = ℏ/Γ ≈ 2.6 �- 10⁻²⁵ s (wider width = shorter life)', W / 2, 228);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Measuring branching ratios tests the Standard Model predictions precisely', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// Chapter 14  -  Scintillation Counters
// ============================================

/** 14.1 – How Scintillation Works */
export const ScintillationMechanism = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('How Scintillation Works', W / 2, 24);

        const steps = [
            { n: '1', title: 'Particle\nenters', desc: 'Charged particle\npasses through\nscintillator', color: '#3B82F6', icon: '→' },
            { n: '2', title: 'Energy\ndeposit', desc: 'Ionizes atoms,\nexcites electrons\nto higher states', color: '#EF4444', icon: '⚡' },
            { n: '3', title: 'De-excitation', desc: 'Electrons fall back\ndown, emitting\nUV/visible photons', color: '#F59E0B', icon: '💡' },
            { n: '4', title: 'Light\ncollection', desc: 'Photons guided to\nphotodetector via\nlight guide', color: '#22C55E', icon: '🔬' },
            { n: '5', title: 'Signal', desc: 'PMT/SiPM converts\nlight to electrical\npulse', color: '#8B5CF6', icon: '📊' },
        ];

        steps.forEach((s, i) => {
            const x = 8 + i * 110, y = 50, w = 102, h = 170;
            ctx.fillStyle = s.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
            ctx.strokeStyle = s.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();

            ctx.beginPath(); ctx.arc(x + w / 2, y + 18, 12, 0, Math.PI * 2);
            ctx.fillStyle = s.color; ctx.fill();
            ctx.fillStyle = 'white'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(s.n, x + w / 2, y + 23);

            ctx.fillStyle = s.color; ctx.font = '18px Inter,sans-serif';
            ctx.fillText(s.icon, x + w / 2, y + 50);

            ctx.fillStyle = s.color; ctx.font = 'bold 11px Inter,sans-serif';
            s.title.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 70 + li * 13));

            ctx.fillStyle = '#475569'; ctx.font = '9px Inter,sans-serif';
            s.desc.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 105 + li * 12));

            if (i < steps.length - 1) {
                ctx.fillStyle = '#94A3B8'; ctx.font = 'bold 14px Inter,sans-serif';
                ctx.fillText('→', x + w + 2, y + h / 2);
            }
        });

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Light yield is proportional to deposited energy → can measure particle energy', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Scintillators are among the most widely used detectors in particle physics', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 14.2 – Organic vs Inorganic Scintillators */
export const OrganicVsInorganic = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Organic vs Inorganic Scintillators', W / 2, 24);

        // Organic card
        ctx.fillStyle = '#DCFCE7'; ctx.beginPath(); ctx.roundRect(20, 45, 245, 185, 12); ctx.fill();
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(20, 45, 245, 185, 12); ctx.stroke();
        ctx.fillStyle = '#16A34A'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Organic', 142, 68);

        const orgProps = [
            'Plastic or liquid',
            'Fast response (~ns)',
            'Low light yield',
            'Low density → thin',
            'Used for: timing, trigger',
            'Examples: BC-408, EJ-200',
        ];
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        orgProps.forEach((p, i) => ctx.fillText('• ' + p, 35, 92 + i * 20));

        // Inorganic card
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(295, 45, 245, 185, 12); ctx.fill();
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(295, 45, 245, 185, 12); ctx.stroke();
        ctx.fillStyle = '#2563EB'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Inorganic', 417, 68);

        const inorgProps = [
            'Crystal (NaI, CsI, BGO)',
            'Slower response (~μs)',
            'High light yield',
            'High density → compact',
            'Used for: calorimetry',
            'Examples: NaI(Tl), PbWO₄',
        ];
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        inorgProps.forEach((p, i) => ctx.fillText('• ' + p, 310, 92 + i * 20));

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('BL4S typically uses organic plastic scintillators for trigger and timing', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 14.3 – Light Yield and Light Guides */
export const LightYieldGuides = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Light Yield, Wavelength & Light Guides', W / 2, 24);

        // Scintillator → Light guide → PMT diagram
        const dy = 110;

        // Scintillator block
        ctx.fillStyle = '#DBEAFE'; ctx.fillRect(30, dy - 30, 120, 60);
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.strokeRect(30, dy - 30, 120, 60);
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Scintillator', 90, dy + 5);

        // Incoming particle
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(10, dy - 15); ctx.lineTo(35, dy - 15); ctx.stroke();
        ctx.fillStyle = '#EF4444'; ctx.beginPath(); ctx.moveTo(35, dy - 15); ctx.lineTo(30, dy - 19); ctx.lineTo(30, dy - 11); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#EF4444'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('particle', 22, dy - 24);

        // Light photons inside
        ctx.fillStyle = '#FCD34D';
        for (let i = 0; i < 5; i++) {
            const px = 55 + i * 18, py = dy - 12 + (i % 3) * 10;
            ctx.beginPath(); ctx.arc(px, py, 3, 0, Math.PI * 2); ctx.fill();
        }

        // Light guide (trapezoidal)
        ctx.fillStyle = '#E0E7FF'; ctx.beginPath();
        ctx.moveTo(150, dy - 30); ctx.lineTo(270, dy - 18);
        ctx.lineTo(270, dy + 18); ctx.lineTo(150, dy + 30);
        ctx.closePath(); ctx.fill();
        ctx.strokeStyle = '#818CF8'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.fillStyle = '#6366F1'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Light Guide', 210, dy + 5);

        // Internal reflection arrows
        ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 1; ctx.setLineDash([2, 2]);
        ctx.beginPath(); ctx.moveTo(155, dy - 10); ctx.lineTo(195, dy + 12); ctx.lineTo(235, dy - 8); ctx.lineTo(265, dy + 5); ctx.stroke();
        ctx.setLineDash([]);

        // PMT
        ctx.fillStyle = '#475569'; ctx.beginPath(); ctx.roundRect(275, dy - 25, 80, 50, 6); ctx.fill();
        ctx.fillStyle = 'white'; ctx.font = 'bold 11px Inter,sans-serif';
        ctx.fillText('PMT /', 315, dy - 4); ctx.fillText('SiPM', 315, dy + 12);

        // Signal out
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(355, dy); ctx.lineTo(395, dy); ctx.stroke();
        ctx.fillStyle = '#22C55E'; ctx.beginPath(); ctx.moveTo(395, dy); ctx.lineTo(390, dy - 4); ctx.lineTo(390, dy + 4); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#22C55E'; ctx.font = '10px Inter,sans-serif';
        ctx.fillText('signal', 405, dy + 4);

        // Stats
        const sy = 160;
        const stats = [
            { label: 'Typical yield', value: '~10,000 photons/MeV', color: '#3B82F6' },
            { label: 'Peak wavelength', value: '~420 nm (blue/UV)', color: '#8B5CF6' },
            { label: 'Light guide', value: 'Total internal reflection', color: '#6366F1' },
        ];
        stats.forEach((s, i) => {
            const x = 15 + i * 185;
            ctx.fillStyle = s.color + '10'; ctx.beginPath(); ctx.roundRect(x, sy, 170, 50, 8); ctx.fill();
            ctx.strokeStyle = s.color; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(x, sy, 170, 50, 8); ctx.stroke();
            ctx.fillStyle = s.color; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(s.label, x + 85, sy + 18);
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            ctx.fillText(s.value, x + 85, sy + 38);
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Wavelength-shifting fibres can shift UV to visible for better PMT coupling', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-indigo-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 14.4 – PMT vs SiPM (case study) */
export const PMTvsSiPM = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('PMT vs SiPM  -  Photodetector Comparison', W / 2, 24);

        // PMT card
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(20, 45, 245, 180, 12); ctx.fill();
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(20, 45, 245, 180, 12); ctx.stroke();
        ctx.fillStyle = '#2563EB'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('PMT (Photomultiplier Tube)', 142, 66);

        const pmtProps = [
            '✓ High gain (~10⁶)',
            '✓ Excellent timing (~ns)',
            '✓ Large active area',
            '�- Bulky, fragile (glass)',
            '�- Needs HV (1-2 kV)',
            '�- Sensitive to magnetic fields',
        ];
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        pmtProps.forEach((p, i) => {
            ctx.fillStyle = p.startsWith('✓') ? '#16A34A' : '#DC2626';
            ctx.fillText(p, 35, 88 + i * 20);
        });

        // SiPM card
        ctx.fillStyle = '#F0FDF4'; ctx.beginPath(); ctx.roundRect(295, 45, 245, 180, 12); ctx.fill();
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(295, 45, 245, 180, 12); ctx.stroke();
        ctx.fillStyle = '#16A34A'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('SiPM (Silicon PM)', 417, 66);

        const sipmProps = [
            '✓ Compact (mm-scale)',
            '✓ Magnetic field immune',
            '✓ Low voltage (~30-70 V)',
            '✓ Robust, solid-state',
            '�- Small active area',
            '�- Dark count rate (noise)',
        ];
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        sipmProps.forEach((p, i) => {
            ctx.fillStyle = p.startsWith('✓') ? '#16A34A' : '#DC2626';
            ctx.fillText(p, 310, 88 + i * 20);
        });

        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(80, 238, 400, 25, 6); ctx.fill();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('BL4S teams commonly use SiPMs  -  compact, affordable, and easy to operate', W / 2, 256);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Both convert photons to electrical signals via the photoelectric effect', W / 2, H - 4);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 14.5 – Detection Efficiency */
export const DetectionEfficiency = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Detection Efficiency', W / 2, 24);

        // Formula
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 16px Inter,sans-serif';
        ctx.fillText('ε = ε_scint �- ε_transport �- ε_PDE �- ε_electronics', W / 2, 54);

        const factors = [
            { name: 'ε_scint', title: 'Scintillation\nEfficiency', desc: 'Fraction of energy\nconverted to light', value: '~5-10%', color: '#3B82F6' },
            { name: 'ε_transport', title: 'Light\nTransport', desc: 'Photons reaching\nthe photodetector', value: '~10-50%', color: '#22C55E' },
            { name: 'ε_PDE', title: 'Photon Detection\nEfficiency', desc: 'QE of PMT or\nPDE of SiPM', value: '~20-40%', color: '#F59E0B' },
            { name: 'ε_elec', title: 'Electronics\nThreshold', desc: 'Signal above\nnoise threshold', value: '~90-99%', color: '#8B5CF6' },
        ];

        factors.forEach((f, i) => {
            const x = 10 + i * 133, y = 72, w = 122, h = 130;
            ctx.fillStyle = f.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
            ctx.strokeStyle = f.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();

            ctx.fillStyle = f.color; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
            f.title.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 18 + li * 13));

            ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif';
            f.desc.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 55 + li * 13));

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif';
            ctx.fillText(f.value, x + w / 2, y + 100);

            if (i < factors.length - 1) {
                ctx.fillStyle = '#94A3B8'; ctx.font = 'bold 14px Inter,sans-serif';
                ctx.fillText('�-', x + w + 4, y + h / 2);
            }
        });

        ctx.fillStyle = '#FEF2F2'; ctx.beginPath(); ctx.roundRect(80, 215, 380, 25, 6); ctx.fill();
        ctx.fillStyle = '#DC2626'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Overall efficiency is the product  -  can be as low as ~1%', W / 2, 232);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Maximising each factor is key to building a high-performance detector', W / 2, H - 6);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 14.10 – Coincidence Logic and Noise Rejection */
export const CoincidenceLogic = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Coincidence Logic  -  Trigger & Noise Rejection', W / 2, 24);

        // Two scintillator diagram
        const dy = 100;

        // Scint A
        ctx.fillStyle = '#DBEAFE'; ctx.fillRect(60, dy - 25, 80, 50);
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.strokeRect(60, dy - 25, 80, 50);
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Scint A', 100, dy + 5);

        // Scint B
        ctx.fillStyle = '#DCFCE7'; ctx.fillRect(200, dy - 25, 80, 50);
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2; ctx.strokeRect(200, dy - 25, 80, 50);
        ctx.fillStyle = '#22C55E'; ctx.font = 'bold 11px Inter,sans-serif';
        ctx.fillText('Scint B', 240, dy + 5);

        // Particle through both
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(20, dy); ctx.lineTo(310, dy); ctx.stroke();
        ctx.fillStyle = '#EF4444'; ctx.beginPath(); ctx.moveTo(310, dy); ctx.lineTo(305, dy - 4); ctx.lineTo(305, dy + 4); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#EF4444'; ctx.font = '10px Inter,sans-serif';
        ctx.fillText('particle', 20, dy - 10);

        // Signals down to AND gate
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(100, dy + 25); ctx.lineTo(100, dy + 55); ctx.lineTo(175, dy + 70); ctx.stroke();
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(240, dy + 25); ctx.lineTo(240, dy + 55); ctx.lineTo(215, dy + 70); ctx.stroke();

        // AND gate
        ctx.fillStyle = '#F59E0B'; ctx.beginPath(); ctx.roundRect(160, dy + 65, 70, 35, 6); ctx.fill();
        ctx.strokeStyle = '#D97706'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(160, dy + 65, 70, 35, 6); ctx.stroke();
        ctx.fillStyle = 'white'; ctx.font = 'bold 13px Inter,sans-serif';
        ctx.fillText('AND', 195, dy + 88);

        // Trigger output
        ctx.strokeStyle = '#8B5CF6'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(195, dy + 100); ctx.lineTo(195, dy + 120); ctx.stroke();
        ctx.fillStyle = '#8B5CF6'; ctx.font = 'bold 11px Inter,sans-serif';
        ctx.fillText('TRIGGER', 195, dy + 138);

        // Right side: logic table
        const tx = 340, ty = 60;
        ctx.fillStyle = '#1E293B'; ctx.beginPath(); ctx.roundRect(tx, ty, 190, 24, [6, 6, 0, 0]); ctx.fill();
        ['A', 'B', 'A AND B', 'Result'].forEach((h, i) => {
            ctx.fillStyle = 'white'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(h, tx + 25 + i * 48, ty + 16);
        });

        const rows = [
            ['1', '1', '1', 'Signal ✓'],
            ['1', '0', '0', 'Noise �-'],
            ['0', '1', '0', 'Noise �-'],
            ['0', '0', '0', ' -  �-'],
        ];
        rows.forEach((row, ri) => {
            const y = ty + 24 + ri * 22;
            ctx.fillStyle = ri % 2 === 0 ? '#F8FAFC' : 'white'; ctx.fillRect(tx, y, 190, 22);
            row.forEach((cell, ci) => {
                ctx.fillStyle = ri === 0 && ci === 3 ? '#16A34A' : '#475569';
                ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
                ctx.fillText(cell, tx + 25 + ci * 48, y + 16);
            });
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Coincidence reduces random noise rate by factor ~(τ_window �- R_noise)', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};
